import { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Search, X } from "lucide-react";
import { type User, userApi, type UpdateUserData } from "../services/userApi";
import { UserForm } from "@/components/UserForm";

import { companyApi } from "../services/companyApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserUIStore } from "@/stores/userStore";
import { useAuthStore } from "@/stores/authStore";
import { Loader } from "@/components/Loader";
import { toast } from "sonner";

export function UsersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { localUser, signOut } = useAuthStore();
  const {
    searchTerm,
    isFormOpen,
    selectedUser,
    formLoading,
    setSearchTerm,
    openEditForm,
    closeForm,
    setFormLoading,
  } = useUserUIStore();

  const companyId = searchParams.get("companyId");

  const {
    data: users = [],
    isLoading: loading,
    error: usersError,
  } = useQuery<User[], Error>({
    queryKey: ["users", companyId],
    queryFn: () => userApi.getAllUsers(companyId || undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const { data: company } = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => companyApi.getCompany(companyId!),
    enabled: !!companyId,
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      userApi.updateUser(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      closeForm();
    },
    onSettled: () => setFormLoading(false),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userApi.deleteUser(userId),
    onSuccess: () => {
      // Query invalidation and success toast are now handled in handleDeleteUser based on isSelfDeletion
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete user", {
        position: "bottom-right",
      });
    },
  });

  const handleUpdateUser = async (userData: UpdateUserData) => {
    if (!selectedUser) return;
    setFormLoading(true);
    await updateUserMutation
      .mutateAsync({ id: selectedUser.id, data: userData })
      .catch(() => undefined);
  };

  const handleDeleteUser = async (userId: string) => {
    // Check if user is trying to delete themselves
    const isSelfDeletion = localUser?.id === userId;

    if (isSelfDeletion) {
      try {
        // Delete the user first
        await deleteUserMutation.mutateAsync(userId);

        // Sign out the user to prevent redirect loops
        toast.success("Your account has been deleted successfully", {
          position: "bottom-right",
        });

        // Clear store and redirect to login
        await signOut();
        navigate("/login", { replace: true });
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error("Delete user error:", error);
      }
    } else {
      // Normal deletion for other users
      try {
        await deleteUserMutation.mutateAsync(userId);

        // Only invalidate queries for non-self deletions
        queryClient.invalidateQueries({ queryKey: ["users"] });

        // Show success toast for non-self deletions
        toast.success("User deleted successfully", {
          position: "bottom-right",
        });
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error("Delete user error:", error);
      }
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users Management</h1>
          {companyId && company && (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-md text-gray-600">
                Filtered by Company:{" "}
                <span className="font-semibold">{company.name}</span>
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/users")}
                className="ml-2"
              >
                <X className="h-3 w-3 mr-1" />
                Show All Users
              </Button>
            </div>
          )}
        </div>
        <Dialog
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) closeForm();
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">Edit User</DialogTitle>
            </DialogHeader>
            <div className="mt-1">
              <UserForm
                user={selectedUser || undefined}
                onSubmit={(data) => {
                  handleUpdateUser(data as UpdateUserData);
                }}
                onCancel={closeForm}
                isLoading={formLoading}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {usersError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {usersError.message}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {companyId
                ? `Company Users (${filteredUsers.length})`
                : `All Users (${filteredUsers.length})`}
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Providers</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-8 text-gray-500"
                  >
                    {searchTerm
                      ? "No users found matching your search."
                      : "No users found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          user.role?.name.includes("admin")
                            ? "default"
                            : "secondary"
                        }
                      >
                        {user.role?.name || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {user.providers.join(", ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.company ? user.company.name : "-"}
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditForm(user)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {localUser?.id === user.id
                                  ? "Delete Your Account"
                                  : "Delete User"}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {localUser?.id === user.id ? (
                                  <>
                                    Are you sure you want to delete your own
                                    account? This will:
                                    <br />• Permanently delete all your data
                                    <br />• Sign you out immediately
                                    <br />• Redirect you to the login page
                                    <br />
                                    <br />
                                    This action cannot be undone.
                                  </>
                                ) : (
                                  <>
                                    Are you sure you want to delete{" "}
                                    {user.firstName} {user.lastName}? This
                                    action cannot be undone.
                                  </>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(user.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {localUser?.id === user.id
                                  ? "Delete My Account"
                                  : "Delete"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
